import { useState, useEffect, useCallback } from 'react';
import { getToken } from 'firebase/messaging';
import { messagingReady } from '../lib/firebase.js';
import { apiClient } from '../lib/api-client.js';
import { useAuth } from '../features/auth/useAuth.js';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function useFcmToken() {
  const { dbUser } = useAuth();

  // messaging の非同期解決を待たず、Notification API の有無だけで初期値を決定する
  const [permission, setPermission] = useState<PermissionState>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission as PermissionState;
  });

  const register = useCallback(async () => {
    if (!VAPID_KEY) return;
    const msg = await messagingReady;
    if (!msg) return;
    try {
      const token = await getToken(msg, { vapidKey: VAPID_KEY });
      if (token) {
        await apiClient.patch('/users/me/fcm-token', { fcmToken: token }, { requireAuth: true });
      }
    } catch (err) {
      console.error('[FCM] トークン登録失敗:', err);
    }
  }, []);

  const requestAndRegister = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const msg = await messagingReady;
    if (!msg) return;
    const result = await Notification.requestPermission();
    setPermission(result as PermissionState);
    if (result === 'granted') {
      await register();
    }
  }, [register]);

  // ログイン済み + 許可済みの場合はマウント時に自動登録
  useEffect(() => {
    if (!dbUser || permission !== 'granted') return;
    void register();
  }, [dbUser, permission, register]);

  return { permission, requestAndRegister };
}
